import type { IGeometryMeasurement } from '../../../domain/geometry-shapes';

/**
 * The GeometryMeasurementInputEvent is dispatched by a GeometryMeasurementInput when the user has given a measurement another length and is done giving it.
 *
 * It stands in for the InputFieldEvents of the field the length is typed in, which the GeometryMeasurementInput keeps to itself: a listener above it hears about a measurement, not about a field, so it never has to know what a measurement is made of.
 *
 * It names the measurement and the length and not the geometry they belong to, because the geometry is the one the position on show is cut to, and that is found where a picked shape finds it — among `ConfiguratorModel.currentParameters`.
 */
export default class GeometryMeasurementInputEvent extends Event {
	/**
	 * The static MEASUREMENT_CHANGED type is dispatched when the user has given a measurement a length other than the one it has.
	 *
	 * Only once the length is settled: typing is not a change until the field is left, and leaving a field with the length it had, or with no length at all, is not one either.
	 *
	 * Example: addEventListener(GeometryMeasurementInputEvent.MEASUREMENT_CHANGED, (event: GeometryMeasurementInputEvent) => {});
	 */
	public static MEASUREMENT_CHANGED = 'GeometryMeasurementInputEvent.measurementChanged';

	/** Which measurement the user changed. */
	public get measurement(): IGeometryMeasurement {
		return this.#measurement;
	}

	/** The length the user gave it, in whole millimetres. */
	public get value(): number {
		return this.#value;
	}

	#measurement: IGeometryMeasurement;
	#value: number;

	public constructor(measurement: IGeometryMeasurement, value: number) {
		super(GeometryMeasurementInputEvent.MEASUREMENT_CHANGED, { bubbles: true });
		this.#measurement = measurement;
		this.#value = value;
	}
}
